import { useState, useCallback } from "react";
import { removeEmoji } from "../edit-customer/utils/validationUtils";
import { validateEmail } from "../utils/validation";

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  alternativePhone: string;
  email: string;
  phones: Phone[];
  emails: emails[];
  address: string;
  address2: string;
  address3: string;
  address4: string;
  osb: string;
}
export interface Phone {
  phone: string;
  isPrimary?: boolean;
}
export interface emails {
  email: string;
  isPrimary?: boolean;
  osb?: string;
}

interface Errors {
  phone: boolean;
  email: boolean;
  duplicatePhone: boolean;
}

const INITIAL_FORM: FormData = {
  firstName: "",
  middleName: "",
  lastName: "",
  phone: "",
  alternativePhone: "",
  email: "",
  phones: [],
  emails: [],
  address: "",
  address2: "",
  address3: "",
  address4: "",
  osb: "",
};

const INITIAL_ERRORS: Errors = {
  phone: false,
  email: false,
  duplicatePhone: false,
};

// --- Utility: clean and limit phone numbers ---
const cleanPhone = (value: string, maxDigits = 8): string => {
  let cleaned = removeEmoji(value).replace(/[^0-9+\s()]/g, "");
  const digits = cleaned.replace(/\D/g, "");

  if (digits.length > maxDigits) {
    cleaned = digits.slice(0, maxDigits);
  }
  return cleaned;
};

export const useCustomerForm = () => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Errors>(INITIAL_ERRORS);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const updateField = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handlePhoneChange = useCallback(
    (value: string, altPhone: string) => {
      const cleanedValue = cleanPhone(value);
      updateField("phone", cleanedValue);

      const mainDigits = cleanedValue.replace(/\D/g, "");
      const altDigits = altPhone.replace(/\D/g, "");

      setErrors((prev) => ({
        ...prev,
        duplicatePhone: mainDigits !== "" && mainDigits === altDigits,
      }));
    },
    [updateField],
  );

  const handleAlternativePhoneChange = useCallback(
    (value: string, mainPhone: string) => {
      const cleanedValue = cleanPhone(value);
      updateField("alternativePhone", cleanedValue);

      const altDigits = cleanedValue.replace(/\D/g, "");
      const mainDigits = mainPhone.replace(/\D/g, "");

      setErrors((prev) => ({
        ...prev,
        duplicatePhone: altDigits !== "" && altDigits === mainDigits,
      }));
    },
    [updateField],
  );

  const validateForm = useCallback(() => {
    const emailError = formData.email ? !validateEmail(formData.email) : false;

    setErrors((prev) => ({ ...prev, email: emailError }));

    return formData.firstName.trim() !== "" && formData.phone.replace(/\D/g, "").length >= 8 && !emailError && !errors.duplicatePhone;
  }, [formData, errors.duplicatePhone]);

  const isFormValid = useCallback(() => {
    return formData.firstName.trim() !== "" && formData.phone.replace(/\D/g, "").length >= 8 && !errors.duplicatePhone && (!formData.email || !errors.email);
  }, [formData, errors]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setErrors(INITIAL_ERRORS);
  }, []);

  return {
    formData,
    errors,
    updateField,
    handlePhoneChange,
    handleAlternativePhoneChange,
    validateForm,
    resetForm,
    isFormValid,
    handleInputChange,
  };
};
